/**
 * Camunda 7.18 Execution REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/execution/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  Execution,
  ExecutionQuery,
  ExecutionTriggerDto,
  CountResult,
  Variables,
  VariableValue,
} from '../types';

export class ExecutionService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/execution';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get executions
   */
  async getList(query?: ExecutionQuery): Promise<Execution[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Execution[]>(`${this.basePath}${params}`);
  }

  /**
   * Get executions count
   */
  async getCount(query?: ExecutionQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  /**
   * Query executions (POST)
   */
  async queryList(query: ExecutionQuery): Promise<Execution[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<Execution[]>(`${this.basePath}${params}`, body);
  }

  /**
   * Query executions count (POST)
   */
  async queryCount(query: ExecutionQuery): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/count`, query);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get execution by ID
   */
  async getById(id: string): Promise<Execution> {
    return this.client.get<Execution>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Signal
  // ============================================================================

  /**
   * Trigger execution (signal event)
   */
  async triggerSignal(id: string, dto?: ExecutionTriggerDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/signal`, dto || {});
  }

  // ============================================================================
  // Message Event
  // ============================================================================

  /**
   * Trigger message event subscription
   */
  async triggerMessageEvent(
    id: string,
    messageName: string,
    variables?: Variables
  ): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/messageSubscriptions/${messageName}/trigger`, {
      variables,
    });
  }

  /**
   * Get message event subscriptions
   */
  async getMessageEventSubscriptions(id: string): Promise<any[]> {
    return this.client.get<any[]>(`${this.basePath}/${id}/messageSubscriptions`);
  }

  /**
   * Get message event subscription
   */
  async getMessageEventSubscription(id: string, messageName: string): Promise<any> {
    return this.client.get<any>(`${this.basePath}/${id}/messageSubscriptions/${messageName}`);
  }

  // ============================================================================
  // Variables
  // ============================================================================

  /**
   * Get execution variables
   */
  async getVariables(
    id: string,
    options?: { deserializeValues?: boolean }
  ): Promise<Variables> {
    const params = options?.deserializeValues !== undefined
      ? `?deserializeValues=${options.deserializeValues}`
      : '';
    return this.client.get<Variables>(`${this.basePath}/${id}/localVariables${params}`);
  }

  /**
   * Get a single execution variable
   */
  async getVariable(
    id: string,
    varName: string,
    options?: { deserializeValue?: boolean }
  ): Promise<VariableValue> {
    const params = options?.deserializeValue !== undefined
      ? `?deserializeValue=${options.deserializeValue}`
      : '';
    return this.client.get<VariableValue>(`${this.basePath}/${id}/localVariables/${varName}${params}`);
  }

  /**
   * Get variable binary data
   */
  async getVariableBinary(id: string, varName: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/${id}/localVariables/${varName}/data`);
  }

  /**
   * Modify execution variables
   */
  async modifyVariables(
    id: string,
    dto: { modifications?: Variables; deletions?: string[] }
  ): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/localVariables`, dto);
  }

  /**
   * Set a single execution variable
   */
  async setVariable(id: string, varName: string, value: VariableValue): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/localVariables/${varName}`, value);
  }

  /**
   * Set variable binary data
   */
  async setVariableBinary(
    id: string,
    varName: string,
    data: Blob,
    valueType?: string
  ): Promise<void> {
    const formData = new FormData();
    formData.append('data', data);
    if (valueType) {
      formData.append('valueType', valueType);
    }
    await this.client.postFormData<void>(
      `${this.basePath}/${id}/localVariables/${varName}/data`,
      formData
    );
  }

  /**
   * Delete an execution variable
   */
  async deleteVariable(id: string, varName: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}/localVariables/${varName}`);
  }

  // ============================================================================
  // Create Incident
  // ============================================================================

  /**
   * Create an incident for an execution
   */
  async createIncident(
    id: string,
    incidentType: string,
    options?: {
      configuration?: string;
      message?: string;
    }
  ): Promise<any> {
    return this.client.post<any>(`${this.basePath}/${id}/create-incident`, {
      incidentType,
      ...options,
    });
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: ExecutionQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else if (typeof value === 'object') {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildPaginationParams(params: {
    firstResult?: number;
    maxResults?: number;
    sortBy?: string;
    sortOrder?: string;
  }): string {
    const urlParams = new URLSearchParams();
    if (params.firstResult !== undefined) urlParams.append('firstResult', String(params.firstResult));
    if (params.maxResults !== undefined) urlParams.append('maxResults', String(params.maxResults));
    if (params.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params.sortOrder) urlParams.append('sortOrder', params.sortOrder);
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let executionServiceInstance: ExecutionService | null = null;

export function getExecutionService(): ExecutionService {
  if (!executionServiceInstance) {
    executionServiceInstance = new ExecutionService();
  }
  return executionServiceInstance;
}

export default ExecutionService;
